// Fetches the record a calendar event links to, for the detail popover (E1).
//
// Deliberately NOT the `getLeadDetail` / `getOpportunityDetail` thunks, even
// though they hit these same two endpoints: both dispatch
// `setEntityLocations(payload.locations)` — a single global "current entity
// locations" — and their `.fulfilled` reducers write the record into the shared
// lead/opportunity adapters. Hydrating a popover is a read for one card, so
// borrowing those would let opening a tile overwrite state another open view is
// using. This issues the same request and returns the body, nothing more.

import { get } from '@user/store/services/crud'
import { ServiceConfig } from '@user/store/services/config'
import type { CalendarEntityType } from '../typings'

/**
 * The slice of the detail payload the popover reads. All three fields are on
 * `IEntityDetails`, which both `ILeadDetail` and `IOpportunityDetail` extend,
 * so one shape covers Lead, PendingLead and Opportunity.
 */
export type EventRecordDTO = {
  primaryContact?: { firstName?: string | null; lastName?: string | null }
  assignee?: { fullName?: string | null } | null
  totalLeadValue?: number
}

// PendingLead resolves through the Lead endpoint — there is no pending-lead
// detail endpoint in this app (E1).
const PATH: Record<CalendarEntityType, string> = {
  Lead: 'Lead',
  PendingLead: 'Lead',
  Opportunity: 'Opportunity',
}

export async function fetchEventRecord(
  entityType: CalendarEntityType,
  entityId: number,
  signal?: AbortSignal,
): Promise<EventRecordDTO> {
  const response = await get(
    `${ServiceConfig.baseAPIUrl}/${PATH[entityType]}/${entityId}`,
    {},
    { signal },
  )
  if (!response.ok) {
    throw new Error(`Event record request failed (${response.status})`)
  }
  return (await response.json()) as EventRecordDTO
}
